import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {answerObjection, DeclutterObjection, fetchObjections} from '../service/DeclutterApi';
import {actionLabel, OBJECTION_CHIPS} from '../support/declutterLabels';
import AuthImage from './AuthImage';
import Spinner from './Spinner';

/** Конкретная вещь, закрывающая ту же функциональную роль (item-decision-consistency, F04). */
interface Alternative {
    itemId: string;
    name: string;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    score: number | null;
    roleNameRu: string | null;
}

/**
 * Ответ с альтернативами: поля добавлены бэкендом аддитивно, поэтому расширяем тип здесь,
 * а не в общем контракте разбора.
 */
type ObjectionView = DeclutterObjection & {
    alternatives?: Alternative[];
    lossSummary?: string | null;
    confidenceLowered?: boolean;
};

/**
 * F04: «Нечем заменить» — сомнение, «Это не замена» — факт после просмотра альтернатив.
 * Второе фиксируется и понижает уверенность вердикта, поэтому это отдельное действие,
 * а не шестой чип в общем ряду.
 */
const NOT_A_REPLACEMENT = 'NOT_A_REPLACEMENT';

/**
 * Возражения по вещи.
 *
 * Разбор сомнения — не спор: приложение отвечает фактами об этой конкретной вещи и повторяет
 * уже вынесенную рекомендацию, не меняя её. Поэтому чипы оформлены нейтрально и не выглядят
 * как альтернатива основным действиям — решение остаётся за человеком.
 */
export default function DeclutterObjections({ownerId, sessionId, itemId}: {
    ownerId: string | null;
    sessionId: string;
    itemId: string;
}) {
    const [answers, setAnswers] = useState<ObjectionView[]>([]);
    const [pending, setPending] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        // Уже разобранные возражения показываются сразу: человек мог вернуться к карточке.
        fetchObjections(ownerId, sessionId, itemId)
            .then(list => {
                if (!cancelled) setAnswers(list);
            })
            .catch(() => {
                if (!cancelled) setAnswers([]);
            });
        return () => {
            cancelled = true;
        };
    }, [ownerId, sessionId, itemId]);

    const ask = useCallback(async (code: string) => {
        setPending(code);
        setError(null);
        try {
            const answer = await answerObjection(ownerId, sessionId, itemId, code);
            setAnswers(prev => [...prev.filter(a => a.objectionCode !== code), answer]);
        } catch {
            setError('Не удалось разобрать возражение. Попробуйте ещё раз.');
        } finally {
            setPending(null);
        }
    }, [ownerId, sessionId, itemId]);

    return (
        <div className="mt-4 border-t border-gray-100 dark:border-ink-700 pt-4"
             data-testid="declutter-objections">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Что-то мешает расстаться?
            </h4>
            <div className="mt-2 flex flex-wrap gap-2">
                {OBJECTION_CHIPS.map(chip => (
                    <button
                        key={chip.code}
                        type="button"
                        disabled={pending !== null}
                        onClick={() => ask(chip.code)}
                        data-testid={`declutter-objection-${chip.code}`}
                        className="btn-secondary min-h-[44px] px-4 rounded-xl text-sm"
                    >
                        {chip.label}
                    </button>
                ))}
            </div>
            {pending && (
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Spinner/> Разбираем…
                </div>
            )}
            {error && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400"
                   data-testid="declutter-objection-error">{error}</p>
            )}
            {/* F04: возражение «нечем заменить» разбирается конкретными вещами, а не числом. */}
            <button
                type="button"
                disabled={pending !== null}
                onClick={() => ask(NOT_A_REPLACEMENT)}
                data-testid={`declutter-objection-${NOT_A_REPLACEMENT}`}
                className="btn-secondary mt-2 min-h-[44px] px-4 rounded-xl text-sm"
            >
                Это не замена
            </button>
            {answers.map(answer => (
                <div key={answer.objectionCode} className="mt-3 rounded-xl bg-gray-50 dark:bg-ink-800 p-3"
                     data-testid={`declutter-objection-answer-${answer.objectionCode}`}>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {answer.objectionLabel}
                    </p>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{answer.answer}</p>
                    {(answer.alternatives ?? []).length > 0 && (
                        <ul className="mt-2 space-y-2" data-testid="declutter-objection-alternatives">
                            {(answer.alternatives ?? []).map(alt => (
                                <li key={alt.itemId} className="flex items-center gap-2">
                                    {alt.primaryPhotoUrl && (
                                        <AuthImage imageKey={alt.primaryPhotoUrl} variant="thumbnail"
                                            alt={alt.name}
                                            className="w-10 h-10 rounded-lg object-cover shrink-0"/>
                                    )}
                                    <Link to={`/clothing/${alt.itemId}`}
                                        className="text-sm text-brand-600 dark:text-brand-400 hover:underline truncate">
                                        {alt.name}
                                    </Link>
                                    {alt.score != null && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                                            {alt.score} из 100
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    {answer.lossSummary && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400"
                           data-testid="declutter-objection-loss">{answer.lossSummary}</p>
                    )}
                    {answer.confidenceLowered && (
                        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400"
                           data-testid="declutter-objection-confidence-lowered">
                            Уверенность рекомендации по этой вещи снижена — решение за вами.
                        </p>
                    )}
                    {answer.revisedAction && (
                        <p className="mt-2 text-sm text-gray-900 dark:text-gray-100">
                            Рекомендация прежняя:{' '}
                            <span className="font-medium">{actionLabel(answer.revisedAction)}</span>
                        </p>
                    )}
                    {answer.limitReached && (
                        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                            Лимит разборов исчерпан — показан сохранённый ответ.
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}
